/**
 * Encurta o endereço que o mapa devolve, para caber numa linha de tabela.
 *
 * O Google entrega o endereço inteiro — "Supermercado Santa Lúcia - Jardim
 * Felicidade I - BR-156 - Jardim Felicidade, Macapá - AP", 88 caracteres. Numa
 * listagem isso quebra em quatro linhas e espreme a coluna inteira.
 *
 * O que sobrevive ao corte são as duas pontas, que é justamente o que
 * identifica o lugar: o **nome** (primeiro trecho) e a **cidade** (último). O
 * miolo é logradouro e bairro, que ninguém lê numa tabela e quem precisar vê no
 * detalhe da viagem — o texto completo continua gravado.
 */

/** Abaixo disto o endereço já cabe, e cortar só tiraria informação. */
const JA_CABE = 30;

export interface LugarResumido {
  /** O nome do lugar: o que identifica. */
  principal: string;
  /** Cidade e UF, quando acrescentam algo ao nome. */
  complemento: string | null;
}

export const resumirLugar = (texto?: string | null): LugarResumido => {
  const limpo = (texto ?? '').trim();

  if (limpo === '') return { principal: '—', complemento: null };
  if (limpo.length <= JA_CABE) return { principal: limpo, complemento: null };

  // O nome vai até o primeiro separador — hífen cercado de espaços ou vírgula.
  // O hífen sem espaços fica: faz parte de nomes como "BR-156" e "São José".
  const nome = limpo.split(/\s+-\s+|,/)[0].trim();

  // A cidade é o último trecho depois da vírgula: "..., Macapá - AP".
  const partes = limpo.split(',');
  const cauda = partes.length > 1 ? partes[partes.length - 1].trim() : '';

  // "Santana - Porto Santana, Santana - Amapá" daria "Santana · Santana -
  // Amapá". Quando a cauda já carrega o nome, ela sozinha diz tudo.
  if (cauda && cauda.toLowerCase().startsWith(nome.toLowerCase())) {
    return { principal: cauda, complemento: null };
  }

  return { principal: nome, complemento: cauda || null };
};

/** Uma linha só, para onde não cabem duas. */
export const lugarEmUmaLinha = (texto?: string | null): string => {
  const { principal, complemento } = resumirLugar(texto);

  return complemento ? `${principal} · ${complemento}` : principal;
};
